'use client';

import React, { forwardRef, useCallback, useContext, useEffect } from 'react';
import { ModalityOptions, useBloodVision } from '../BloodVisionContext';
import SelectionSection from './SelectionSection';
import { ZipCodeContext } from './ZipCodeContext';
import { focusZipCodeInput } from './checkoutGuidance';
import { BloodVisionExperienceModal } from './BloodVisionExperienceModal';
import { ImageModal } from 'components/ring-air/ProductSelectionSection/ImageModal';
import { CustomImage } from 'components/index';
import styles from './ModalitySection.module.css';

const BulletDisclaimer = (props: {
  className?: string;
  title?: string;
  backgroundColor?: string;
  description?: string;
  bulletPoints?: ReadonlyArray<string>;
}) => {
  return (
    <div
      className={`${styles.bulletDisclaimer}${
        props.className ? ` ${props.className}` : ''
      }`}
      style={
        props.backgroundColor
          ? ({ ['--bd-bg']: props.backgroundColor } as React.CSSProperties)
          : undefined
      }
    >
      {props.title ? <h6>{props.title}</h6> : null}
      {props.description ? <p>{props.description}</p> : null}
      {props.bulletPoints && props.bulletPoints.length > 0 ? (
        <ul>
          {props.bulletPoints.map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

interface ModalitySectionProps {
  isCampaignActive?: boolean;
}

const ModalitySection = forwardRef<HTMLDivElement, ModalitySectionProps>(
  ({ isCampaignActive = false }, ref) => {
    const {
      availableModalityOptions,
      selectedModality,
      setSelectedModality,
      selectionRequiredHint,
      hasUserProvidedZip,
      isZipValid,
      zipCode,
    } = useBloodVision();
    const { zipCodeInputRef } = useContext(ZipCodeContext);

    const isZipLocked =
      !hasUserProvidedZip || !zipCode || zipCode.length !== 5 || !isZipValid;

    const handleFocusZipInput = useCallback(() => {
      focusZipCodeInput(zipCodeInputRef);
    }, [zipCodeInputRef]);

    // Auto-select the only available modality if there is just one
    // Also handle campaign active case where AT_HOME should be auto-switched to WALK_IN
    useEffect(() => {
      // If campaign is active and AT_HOME is selected, switch to WALK_IN if available
      if (
        isCampaignActive &&
        selectedModality === ModalityOptions.AT_HOME &&
        availableModalityOptions.includes(ModalityOptions.WALK_IN)
      ) {
        setSelectedModality(ModalityOptions.WALK_IN);
      }
      // Auto-select the only available modality if there is just one
      else if (
        availableModalityOptions &&
        availableModalityOptions.length === 1 &&
        !selectedModality
      ) {
        setSelectedModality(availableModalityOptions[0]);
      }
    }, [
      availableModalityOptions,
      selectedModality,
      setSelectedModality,
      isCampaignActive,
    ]);

    const modalityOptions = [
      {
        label: 'At Home test',
        value: ModalityOptions.AT_HOME,
        description: 'Nurse visit at your preferred location.',
        icon: (
          <CustomImage
            src={'web_v2/blood-vision/icons/at-home.png'}
            alt="Home Icon"
            width={16}
            height={16}
          />
        ),
      },
      {
        label: 'Lab test',
        value: ModalityOptions.WALK_IN,
        description: 'No extra cost. Labs across the U.S.',
        icon: (
          <CustomImage
            src={'web_v2/blood-vision/icons/at-lab.png'}
            alt="Lab Icon"
            width={16}
            height={16}
          />
        ),
      },
    ];

    const BloodVisionClient = useBloodVision();

    // Calculate disabled options: include AT_HOME if campaign is active
    const disabledOptions = React.useMemo(() => {
      if (isZipLocked) {
        return Object.values(ModalityOptions);
      }

      const baseDisabled = Object.values(ModalityOptions).filter(
        (option) => !availableModalityOptions.includes(option),
      );

      // Add AT_HOME to disabled options if campaign is active
      if (isCampaignActive && !baseDisabled.includes(ModalityOptions.AT_HOME)) {
        return [...baseDisabled, ModalityOptions.AT_HOME];
      }

      return baseDisabled;
    }, [availableModalityOptions, isCampaignActive, isZipLocked]);

    return (
      <div ref={ref}>
        <SelectionSection
          title="Testing Location"
          options={modalityOptions}
          selectedValue={selectedModality || ''}
          onSelect={(value) => setSelectedModality(value as ModalityOptions)}
          disabledOptions={disabledOptions}
          showInfoButton={true}
          infoModalSecond={
            <ImageModal>
              <BloodVisionExperienceModal />
            </ImageModal>
          }
          dataButtonType="blood-vision modality selector"
          dataProduct="Ultrahuman Blood Vision"
          variant="card"
          showRequiredHint={selectionRequiredHint === 'modality'}
          onDisabledOptionClick={
            isZipLocked ? handleFocusZipInput : undefined
          }
        />
        {BloodVisionClient.zipDisclaimer ? (
          <BulletDisclaimer
            title={BloodVisionClient.zipDisclaimer.title}
            // backgroundColor={BloodVisionClient.zipDisclaimer.background_color}
            description={BloodVisionClient.zipDisclaimer.description}
            bulletPoints={BloodVisionClient.zipDisclaimer.bullet_points}
          />
        ) : null}
      </div>
    );
  },
);

ModalitySection.displayName = 'ModalitySection';

export type ModalitySectionRef = React.RefObject<HTMLDivElement>;

export default ModalitySection;
